import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loader from '../../../../component/common/Loader';
import SearchInput from '../../../../component/common/Input/SearchInput';
import Table from '../../../../component/common/Table';
import Pagination from '../../../../component/common/table2/Pagination';
import { fetchGuidedPathUsers } from './companyGuidedPathApi';
import { TableCard } from '../../../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Searchable people list for the Guided Path section — ENGAGEMENT ONLY.
 *
 * Every column is a count or a date: how many plans somebody holds, of which
 * kinds, when they started, how many separate days they ticked something on,
 * how many rituals that adds up to, and when they were last active. That is the
 * adoption question, and it is a fair one for a company that paid for the
 * benefit to ask.
 *
 * WHAT IS NOT HERE, AND WILL NOT BE: the plan's name, its description, its
 * daily expectation, any ritual, and any of the questionnaire answers behind a
 * custom plan. A plan name is the first fifteen characters of what the person
 * typed into the prompt, and the questionnaire is them writing up to five
 * hundred characters on why they need this. There is no row action and no
 * detail page — the arrow most other sections' tables carry is deliberately
 * absent, and the backend has no endpoint behind it.
 *
 * THE KIND OF PLAN IS SHOWN AND THE PLAN ITSELF IS NOT. "Two AI plans" says
 * which product surface somebody used. The name of one of them says what they
 * are going through.
 *
 * Everybody in the population is listed, including people who have never
 * started a plan; they show dashes rather than zeros, so "never started" reads
 * differently from "started one and stopped".
 */
const GuidedPathUsersTable = ({
  fetcher = fetchGuidedPathUsers,
  tableName,
  showCompanyColumn = false,
  department = ''
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [failed, setFailed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({ page, perPage: PER_PAGE, search, department });
        const mapped = (response.data || []).map((row) => {
          const hasPlan = row.plans > 0;
          const cell = (value) => (hasPlan ? Number(value || 0).toLocaleString() : '-');
          return {
            ...row,
            plansText: cell(row.plans),
            aiText: cell(row.aiPlans),
            customText: cell(row.customPlans),
            activeDaysText: cell(row.activeDays),
            ritualsText: cell(row.ritualsTicked),
            startedText: row.started || '-',
            lastActiveText: row.lastActive || '-'
          };
        });
        setRows(mapped);
        setTotalCount(response.totalCount || 0);
        setFailed(false);
      } catch (error) {
        console.error('GuidedPathUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
        // Kept distinct from an empty result: "nobody matched" and "we could
        // not ask" look identical in a bare table, and only one of them is a
        // fact about your people.
        setFailed(true);
      } finally {
        setLoader(false);
      }
    },
    [fetcher, department]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    { title: 'Plans', key: 'plansText', sortable: false, align: 'center', type: 'text3' },
    // Which surface, never which plan.
    { title: 'AI', key: 'aiText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Custom', key: 'customText', sortable: false, align: 'center', type: 'text3' },
    {
      title: 'Days active',
      key: 'activeDaysText',
      sortable: false,
      align: 'center',
      type: 'text3'
    },
    { title: 'Rituals ticked', key: 'ritualsText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Started', key: 'startedText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Last active', key: 'lastActiveText', sortable: false, align: 'center', type: 'text3' }
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      

      {failed ? (
        <p className="mt-3 text-xs text-shoorah-secondary dark:text-gray-400">
          This list could not be loaded, so it is empty rather than showing nobody. Refreshing
          usually fixes it.
        </p>
      ) : null}

      <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
        Counts and dates only. No plan name, description or ritual appears on this page, and there
        is nothing to open behind a row — what somebody&rsquo;s plan is about is theirs, that they
        are using one is what the company is counting.
      </p>
    </div>
  );
};

GuidedPathUsersTable.propTypes = {
  fetcher: PropTypes.func,
  tableName: PropTypes.string,
  showCompanyColumn: PropTypes.bool,
  department: PropTypes.string
};

export default GuidedPathUsersTable;
